import React, { useEffect, useState } from 'react';
import { ArrowRight, Sparkles, RotateCcw, CheckCircle2, AlertCircle } from 'lucide-react';
import type { ColumnMapping, CRMField } from '../types';

interface ColumnMapperProps {
  csvColumns: string[];
  crmFields: CRMField[];
  mappings: ColumnMapping[];
  onMappingsChange: (mappings: ColumnMapping[]) => void;
}

const ColumnMapper: React.FC<ColumnMapperProps> = ({
  csvColumns,
  crmFields,
  mappings,
  onMappingsChange
}) => {
  const [localMappings, setLocalMappings] = useState<ColumnMapping[]>(mappings);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);

  useEffect(() => {
    setLocalMappings(mappings);
  }, [mappings]);

  // Auto-map similar column names
  const autoMap = () => {
    const newMappings: ColumnMapping[] = csvColumns.map((csvCol) => {
      const csvColLower = csvCol.toLowerCase().replace(/[_\s-]/g, '');

      // Find matching CRM field
      const matchedField = crmFields.find((crmField) => {
        const crmFieldLower = crmField.field.toLowerCase().replace(/[_\s-]/g, '');
        const crmLabelLower = crmField.label.toLowerCase().replace(/[_\s-]/g, '');

        return (
          csvColLower === crmFieldLower ||
          csvColLower === crmLabelLower ||
          csvColLower.includes(crmFieldLower) ||
          crmFieldLower.includes(csvColLower)
        );
      });

      return {
        csvColumn: csvCol,
        crmField: matchedField?.field || '',
        crmFieldUuid: matchedField?.uuid
      };
    });

    setLocalMappings(newMappings);
    onMappingsChange(newMappings);
    validateMappings(newMappings);
  };

  // Reset all mappings
  const resetMappings = () => {
    const emptyMappings: ColumnMapping[] = csvColumns.map((col) => ({
      csvColumn: col,
      crmField: '',
      crmFieldUuid: undefined
    }));
    setLocalMappings(emptyMappings);
    onMappingsChange(emptyMappings);
    setValidationErrors([]);
  };

  // Handle mapping change
  const handleMappingChange = (csvColumn: string, crmField: string) => {
    // Find the CRM field to get its UUID
    const selectedField = crmFields.find(f => f.field === crmField);

    const newMappings = localMappings.map((mapping) =>
      mapping.csvColumn === csvColumn
        ? {
            ...mapping,
            crmField,
            crmFieldUuid: selectedField?.uuid
          }
        : mapping
    );
    setLocalMappings(newMappings);
    onMappingsChange(newMappings);
    validateMappings(newMappings);
  };

  // Validate mappings
  const validateMappings = (mappings: ColumnMapping[]) => {
    const errors: string[] = [];

    // Check if required fields are mapped
    const requiredFields = crmFields.filter((f) => f.required);
    requiredFields.forEach((field) => {
      const isMapped = mappings.some((m) => m.crmField === field.field);
      if (!isMapped) {
        errors.push(`Required field "${field.label}" is not mapped`);
      }
    });

    // Check for duplicate mappings
    const mappedFields = mappings.filter((m) => m.crmField).map((m) => m.crmField);
    const duplicates = mappedFields.filter((field, index) => mappedFields.indexOf(field) !== index);
    if (duplicates.length > 0) {
      errors.push(`Duplicate mappings found for: ${duplicates.join(', ')}`);
    }

    setValidationErrors(errors);
  };

  // Get mapped count
  const mappedCount = localMappings.filter((m) => m.crmField).length;

  // Check if a CRM field is already mapped
  const isFieldMapped = (fieldName: string, currentCsvColumn: string) => {
    return localMappings.some(
      (m) => m.crmField === fieldName && m.csvColumn !== currentCsvColumn
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
            Map Columns
          </h3>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Match your CSV columns to CRM fields. Required fields are marked with an asterisk (*).
          </p>
        </div>

        <div className="flex space-x-2">
          <button
            type="button"
            onClick={autoMap}
            className="flex items-center space-x-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors text-sm font-medium"
          >
            <Sparkles className="h-4 w-4" />
            <span>Auto-map</span>
          </button>
          <button
            type="button"
            onClick={resetMappings}
            className="flex items-center space-x-2 px-4 py-2 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 rounded-lg transition-colors text-sm font-medium"
          >
            <RotateCcw className="h-4 w-4" />
            <span>Reset</span>
          </button>
        </div>
      </div>

      {/* Mapping Stats */}
      <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <div className="p-2 bg-blue-100 dark:bg-blue-800 rounded-full">
            <CheckCircle2 className="h-5 w-5 text-blue-600 dark:text-blue-400" />
          </div>
          <div>
            <p className="text-sm font-medium text-blue-900 dark:text-blue-100">
              {mappedCount} of {csvColumns.length} columns mapped
            </p>
            <p className="text-xs text-blue-700 dark:text-blue-300">
              {crmFields.filter((f) => f.required).length} required fields
            </p>
          </div>
        </div>
      </div>

      {/* Validation Errors */}
      {validationErrors.length > 0 && (
        <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
          <div className="flex items-start space-x-3">
            <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-medium text-red-800 dark:text-red-200 mb-2">
                Please fix the following errors:
              </p>
              <ul className="text-sm text-red-700 dark:text-red-300 space-y-1 list-disc list-inside">
                {validationErrors.map((error, index) => (
                  <li key={index}>{error}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Mapping Table */}
      <div className="space-y-3">
        {localMappings.map((mapping, index) => (
          <div
            key={index}
            className="flex items-center space-x-4 p-4 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg hover:shadow-md transition-shadow"
          >
            {/* CSV Column */}
            <div className="flex-1">
              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                CSV Column
              </label>
              <div className="px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg">
                <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                  {mapping.csvColumn}
                </p>
              </div>
            </div>

            {/* Arrow */}
            <div className="pt-6">
              <ArrowRight className="h-5 w-5 text-gray-400 dark:text-gray-600" />
            </div>

            {/* CRM Field Selector */}
            <div className="flex-1">
              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                CRM Field
              </label>
              <select
                value={mapping.crmField}
                onChange={(e) => handleMappingChange(mapping.csvColumn, e.target.value)}
                className="w-full px-3 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg text-sm text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">-- Skip this column --</option>
                {crmFields.map((field) => {
                  const alreadyMapped = isFieldMapped(field.field, mapping.csvColumn);
                  return (
                    <option
                      key={field.field}
                      value={field.field}
                      disabled={alreadyMapped}
                    >
                      {field.label}
                      {field.required && ' *'}
                      {alreadyMapped && ' (already mapped)'}
                    </option>
                  );
                })}
              </select>
            </div>
          </div>
        ))}
      </div>

      {/* CRM Fields Legend */}
      <div className="p-4 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg">
        <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">
          Available CRM Fields
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
          {crmFields.map((field) => (
            <div
              key={field.field}
              className="flex items-center justify-between px-3 py-2 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded"
            >
              <span className="text-sm text-gray-700 dark:text-gray-300">
                {field.label}
                {field.required && <span className="text-red-500 ml-1">*</span>}
              </span>
              <span className="text-xs text-gray-500 dark:text-gray-400 capitalize">
                {field.type}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ColumnMapper;
